import Styles from './styles.module.css';
import React, { useState } from 'react';
import {
  EmailAuthProvider, getAuth, reauthenticateWithCredential, updatePassword,
} from 'firebase/auth';

type Props = {
  onClose: () => void;
};

// 審査員は管理者(サロン)が発行した初期パスワードでログインするため、
// ログイン後に本人がここでパスワードを変更できるようにする。
// updatePassword()は直近のログインから時間が経つと auth/requires-recent-login で
// 失敗するため、先に現在のパスワードで再認証してから変更する。
const ChangePassword: React.FC<Props> = ({ onClose }) => {

  const [ currentPassword, setCurrentPassword ] = useState('');
  const [ newPassword, setNewPassword ] = useState('');
  const [ newPasswordConfirm, setNewPasswordConfirm ] = useState('');
  const [ error, setError ] = useState('');
  const [ saving, setSaving ] = useState(false);
  const [ done, setDone ] = useState(false);

  async function save() {
    setError('');

    if (!currentPassword || !newPassword || !newPasswordConfirm) {
      setError('すべての項目を入力してください。');
      return;
    }
    if (newPassword.length < 8) {
      setError('新しいパスワードを8文字以上入力してください。');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('新しいパスワードが一致しません。');
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user?.email) {
      setError('ログイン状態を確認できませんでした。再度ログインしてください。');
      return;
    }

    setSaving(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setDone(true);
    } catch (err: any) {
      setError(
        err?.code === 'auth/invalid-credential' || err?.code === 'auth/wrong-password'
          ? '現在のパスワードが正しくありません。'
          : 'パスワードの変更に失敗しました。時間をおいて再度お試しください。'
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={Styles.modalBox}>
      <div className={Styles.modalBackdrop} onClick={() => !saving && onClose()} />
      <div className={Styles.modalContents}>
        <div className={Styles.modalTitleRow}>
          <h4>パスワードの変更</h4>
          <button type="button" className={Styles.modalClose} onClick={onClose}>×</button>
        </div>

        {done ? (
          <>
            <p className={Styles.modalDesc}>パスワードを変更しました。</p>
            <button type="button" className={Styles.modalSubmit} onClick={onClose}>閉じる</button>
          </>
        ) : (
          <>
            <p className={Styles.modalLabel}>現在のパスワード</p>
            <input
              className={Styles.modalInput}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={saving}
            />

            <p className={Styles.modalLabel}>新しいパスワード（8文字以上）</p>
            <input
              className={Styles.modalInput}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={saving}
            />

            <p className={Styles.modalLabel}>新しいパスワード（確認）</p>
            <input
              className={Styles.modalInput}
              type="password"
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              disabled={saving}
            />

            {error && <p className={Styles.modalError}>{error}</p>}

            <button type="button" className={Styles.modalSubmit} onClick={save} disabled={saving}>
              {saving ? '変更中...' : 'パスワードを変更する'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangePassword;
